import { isPlanCode } from "@imagora/contracts/pricing";
import { useCallback } from "react";
import { useFormat, useI18n } from "../i18n";
import { refusedCapability } from "../features/ai-keys/view";
import { isStopRefusal } from "../features/billing/view";
import { brokenRuleIds } from "../features/compliance/rules";
import { ApiError } from "./api";

/** Turns any failure into a sentence a person can read. Codes the UI does not know get the general message. */
export function useErrorMessage() {
  const { t, tCode, tCodeOrNull } = useI18n();
  const { date } = useFormat();
  return useCallback(
    (error: unknown): string => {
      // The trial's stop answers `billing_required` too, but with its own story: everything is stopped until a payment.
      const code = error instanceof ApiError ? (isStopRefusal(error) ? "trial_unpaid" : error.code) : "unknown";
      const message = tCodeOrNull("errors", code) ?? t("errors.unknown");
      // A refusal under the rules says which rules: without that, nobody knows what to change.
      const rules = error instanceof ApiError ? brokenRuleIds(error.details) : [];
      const reasons = [...new Set(rules.map((id) => tCodeOrNull("compliance.rule", id) ?? t("compliance.ruleOther")))];
      // A refusal on the workspace's own keys says what it needed: pictures can stop while texts go on.
      const capability = error instanceof ApiError && code.startsWith("ai_key") ? refusedCapability(error.details) : null;
      if (capability) reasons.push(t("keys.refusedFor", { capability: tCode("keys.capability", capability) }));
      // A refusal by the plan says which plan has it, so the person knows where to go.
      const minPlan = error instanceof ApiError && code === "plan_upgrade_required" ? planOf(error.details) : null;
      if (minPlan) reasons.push(t("plans.error.minPlan", { plan: tCodeOrNull("plans.name", minPlan) ?? minPlan }));
      // A paid workspace refused by a company in its trial is told the day the move works again.
      const trialEnd = error instanceof ApiError && code === "workspace_move_into_trial" ? endsAtOf(error.details) : null;
      if (trialEnd) reasons.push(t("trial.move.until", { date: date(trialEnd) }));
      return [message, ...reasons].join(" ");
    },
    [t, tCode, tCodeOrNull, date],
  );
}

function planOf(details: unknown): string | null {
  if (typeof details !== "object" || details === null) return null;
  const minPlan = (details as { minPlan?: unknown }).minPlan;
  return typeof minPlan === "string" && isPlanCode(minPlan) ? minPlan : null;
}

function endsAtOf(details: unknown): string | null {
  if (typeof details !== "object" || details === null) return null;
  const endsAt = (details as { endsAt?: unknown }).endsAt;
  return typeof endsAt === "string" && !Number.isNaN(Date.parse(endsAt)) ? endsAt : null;
}
